import { z } from 'zod';
import { ActivityRecordKindSchema, AppointmentTypeSchema } from '../activity/activity-records.schemas';
import { commaSeparated } from '../common/comma-separated';
import { queryArray } from '../common/query-array';
import { DealAttentionFlagSchema, DealPhaseSchema, DealStatusSchema } from './deals.schemas';
import { refersTo } from '../common/refers-to';

/**
 * The Deal-Überblick (IMM-430.7) — the manager surface the whole feature was
 * for. One row per deal in scope, carrying everything the table renders so the
 * page needs no follow-up read per row.
 */

/**
 * The deal's next step, or null when it has none — which is itself the signal
 * behind the „Kein nächster Schritt" flag, so the null case is meaningful rather
 * than missing data.
 */
export const DealOverviewNextActivitySchema = z.object({
  activityId: z.uuid(),
  kind: ActivityRecordKindSchema,
  // Set only for an APPOINTMENT, mirroring the store's own shape.
  appointmentType: AppointmentTypeSchema.nullable(),
  content: z.string(),
  dueAt: z.iso.datetime(),
  // Derived at read time against the server's clock, exactly as the deal's
  // „Überfällig" badge is — never stored.
  overdue: z.boolean(),
});
export type DealOverviewNextActivity = z.infer<typeof DealOverviewNextActivitySchema>;

/** The board's batch bound (IMM-1313): the web app chunks its deal ids by it. */
export const LIST_DEAL_NEXT_ACTIVITIES_MAX_IDS = 100;

export const ListDealNextActivitiesPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const ListDealNextActivitiesQuerySchema = z
  .object({
    dealIds: commaSeparated(z.uuid()).refine(
      (ids) => ids.length >= 1 && ids.length <= LIST_DEAL_NEXT_ACTIVITIES_MAX_IDS,
    ),
  })
  .strict();

export const DealNextActivityItemSchema = z.object({
  dealId: refersTo('deal', z.uuid()),
  // Null when the deal has nothing OPEN: every open activity carries a due date
  // (`activity_records` check, schema.ts:227), so „no next activity" is „nothing open".
  nextActivity: DealOverviewNextActivitySchema.nullable(),
});

export const ListDealNextActivitiesResponseSchema = z.object({ data: z.array(DealNextActivityItemSchema) });

export type ListDealNextActivitiesQuery = z.infer<typeof ListDealNextActivitiesQuerySchema>;
export type DealNextActivityItem = z.infer<typeof DealNextActivityItemSchema>;
export type ListDealNextActivitiesResponse = z.infer<typeof ListDealNextActivitiesResponseSchema>;

const DealOverviewItemObject = z.object({
  dealId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  // Resolved through the property module's cross-module read; null when the
  // Objekt cannot be resolved, so one unresolvable row cannot fail the page.
  propertyAddress: z.string().nullable(),
  primaryOwnerName: z.string().nullable(),
  phase: DealPhaseSchema,
  // The deal's lifecycle status. A LOST deal keeps the phase it was lost in as
  // `phase`; the page renders it as „Verloren" from this field (IMM-1048).
  status: DealStatusSchema,
  // IMM-430.6's server-derived counter — the same figure the deal list, the
  // board and the deal header render, so the manager never sees two numbers.
  activeDaysInPhase: z.number().int().min(0),
  // The phase's „zu lange" threshold, so the table can render „18 / 5" without
  // re-deriving the product table client-side. Null for a phase that has none.
  phaseThresholdDays: z.number().int().min(1).nullable(),
  responsibleMemberIds: z.array(refersTo('member', z.uuid())),
  responsibleMemberNames: z.array(z.string()),
  nextActivity: DealOverviewNextActivitySchema.nullable(),
  // Null for a deal that is not ACTIVE — a held or closed deal is not stalled
  // (IMM-430.6). Such deals still appear, and still show their day count.
  attentionFlag: DealAttentionFlagSchema.nullable(),
});

export type DealOverviewItem = z.infer<typeof DealOverviewItemObject>;
export const DealOverviewItemSchema: z.ZodType<DealOverviewItem> = DealOverviewItemObject;

/**
 * Workspace-addressed, not organisation-addressed. The Überblick spans every
 * Standort the caller may see — which is what `attachVisibleOrganizations`
 * resolves, and that middleware reads `:workspaceId` from the path. An
 * org-addressed route cannot carry it (see the slice's Amendment 6).
 */
export const ListDealOverviewPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});
export type ListDealOverviewPathParams = z.infer<typeof ListDealOverviewPathParamsSchema>;

/**
 * Every filter is a repeatable query parameter, and every one of them NARROWS.
 * `standortIds` in particular can only ever intersect the Standorte the caller
 * may already see — it is a view control, never a way to reach further.
 *
 * All four go through `queryArray`, not a bare `z.array(...)`. A query string
 * cannot express „a list of one", so a single occurrence arrives as a scalar and
 * a bare array schema answers 400 before the handler runs — and the typed
 * client comma-joins a multi-value array into ONE occurrence, so that form has
 * to be accepted too (IMM-665, BL-071). The helper is applied to every param
 * rather than only the two carrying a control today: `assigneeMemberIds` gets a
 * single select in IMM-629 and would hit the identical rejection.
 *
 * `createdFrom`, `enteredPhase`, `enteredFrom`, `withoutValuation`, `unassigned` and `dealIds` are link-only (the
 * dashboard's figures open the page with them) and narrow like every other filter.
 */
export const ListDealOverviewQuerySchema = z
  .object({
    standortIds: queryArray(z.uuid()).optional(),
    assigneeMemberIds: queryArray(z.uuid()).optional(),
    phases: queryArray(DealPhaseSchema).optional(),
    attentionFlags: queryArray(DealAttentionFlagSchema).optional(),
    // `LOST` in the set is what brings lost deals into the read; without it the read is open deals only (IMM-1312).
    statuses: queryArray(DealStatusSchema).optional(),
    createdFrom: z.iso.datetime().optional(),
    enteredPhase: DealPhaseSchema.optional(),
    enteredFrom: z.iso.datetime().optional(),
    withoutValuation: z.stringbool().optional(),
    unassigned: z.stringbool().optional(),
    // Exact deal set (dashboard Team table). Ids outside the caller's scope match nothing.
    dealIds: queryArray(z.uuid()).optional(),
  })
  .refine((query) => query.enteredFrom === undefined || query.enteredPhase !== undefined, { path: ['enteredFrom'] });
export type ListDealOverviewQuery = z.infer<typeof ListDealOverviewQuerySchema>;

const ListDealOverviewResponseObject = z.object({
  items: z.array(DealOverviewItemSchema),
  /**
   * Deals per phase, EXCLUDING status LOST, counted over the filtered set MINUS
   * the phase filter itself — otherwise every other bucket reads zero exactly
   * when the group-by view is being used to navigate between them. A lost deal
   * is counted once, in `lostCount`, never under the phase it was lost in
   * (IMM-1048).
   */
  phaseCounts: z.record(DealPhaseSchema, z.number().int().min(0)),
  /**
   * LOST deals in the member's scope — an unbounded count, returned whether or not the read lists them
   * (the „Verloren" option's badge, IMM-1312). Display filters do not narrow it.
   */
  lostCount: z.number().int().min(0),
  /**
   * True when the scope held more than the handler's bound. The overview is
   * deliberately NOT paginated — the attention flag is derived, so it can be
   * neither a `WHERE` nor an `ORDER BY`, and sorting or filtering a page by it
   * would make the ordering page-local rather than global. The bound is a safety
   * net, and it is surfaced rather than silent.
   */
  truncated: z.boolean(),
});

export type ListDealOverviewResponse = z.infer<typeof ListDealOverviewResponseObject>;
export const ListDealOverviewResponseSchema: z.ZodType<ListDealOverviewResponse> = ListDealOverviewResponseObject;

/**
 * The bound on the scan. A Standort holds hundreds of active deals, so this is a
 * safety net rather than a paging mechanism — see the slice's decision on why
 * the surface is not cursor-paginated.
 */
export const OVERVIEW_MAX_DEALS = 2000;
